import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as domainLogosSelectors from 'app-components/flux/domain-logos/domainLogosSelectors';
import { fetchDomainLogos } from 'app-components/flux/domain-logos/domainLogosSlice';
import type RootState from 'app-components/utils/redux-store/rootState';

import type { Biller } from './billPresentmentTypes';
import { extractHostname } from './billPresentmentUtils';

export const useBillersWithLogos = (billers: Biller[] | null) => {
  const dispatch = useDispatch();

  const domainLogos = useSelector(domainLogosSelectors.stdSliceSelectors.selectEntities);
  const fetchedDomains = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!billers || billers.length === 0) return;

    const domainsToFetch = billers.map((b: Biller) => extractHostname(b));
    const newDomains = domainsToFetch.filter((domain) => {
      if (!domain) return false;
      const alreadyFetched = fetchedDomains.current.has(domain);
      const notInStore = !domainLogos?.[domain];
      return !alreadyFetched && notInStore;
    });

    if (newDomains.length > 0) {
      newDomains.forEach((d) => fetchedDomains.current.add(d));
      dispatch(fetchDomainLogos(newDomains) as any);
    }
  }, [billers, dispatch, domainLogos]);
  const selectBillersWithLogos = domainLogosSelectors.makeSelectItemsWithLogos<Biller>((biller) =>
    extractHostname(biller),
  );
  const enrichedBillers = useSelector((state: RootState) => (billers ? selectBillersWithLogos(state, billers) : []));
  const isLoading = useSelector((state: RootState) => domainLogosSelectors.getIsLoading(state));
  const error = useSelector((state: RootState) => domainLogosSelectors.getError(state));
  return { billersWithLogos: enrichedBillers, isLoading, error };
};
